import { Injectable } from '@angular/core';

import { FirebaseAuthentication } from '@capacitor-firebase/authentication';
import { Observable } from 'rxjs';

import {
    Auth,
    authState,
    OAuthProvider,
    FacebookAuthProvider,
    getRedirectResult,
    signInWithPopup,
    signInWithRedirect,
    createUserWithEmailAndPassword,
    signInWithEmailAndPassword,
    sendEmailVerification,
    sendPasswordResetEmail,
    confirmPasswordReset,
    linkWithCredential,
    unlink,
    AuthCredential,
    User as FirebaseUser,
    UserCredential
} from '@angular/fire/auth';

import { UserService } from '../userService/user-service';

export type DevicePlatform = 'android' | 'ios' | 'web';

@Injectable()
export class SocialSigninService {
    private provider = new OAuthProvider('google.com');

    constructor(
        private auth: Auth,
        public userService: UserService
    ) {
        this.provider.setCustomParameters({
            prompt: 'select_account'
        });
    }

    handleCredentialResponse(token) {
        this.userService.login({ token });
        console.log('Token sent to server');
    }

    async googleLogin(platform: DevicePlatform) {
        try {
            console.log('googleLogin', platform);
            let token: string;
            switch (platform) {
                case 'web':
                    token = await signInWithPopup(this.auth, this.provider).then((cred) => cred.user.getIdToken());
                    break;
                case 'android':
                    console.log('googleLogin android: signOut');
                    await FirebaseAuthentication.signOut();
                    // If the next line fails with "error 10", check that you've added
                    // the debug key to the Android app in the Firebase admin.  To generate
                    // the SHA1 key, run the following command:
                    // keytool -exportcert -list -v -alias androiddebugkey -keystore ~/.android/debug.keystore
                    console.log('googleLogin android: signInWithGoogle');
                    await FirebaseAuthentication.signInWithGoogle();
                    console.log('googleLogin android: getIdToken');
                    token = (await FirebaseAuthentication.getIdToken()).token;
                    console.log('googleLogin android: token', token);
                    break;
                case 'ios':
                    // Not working?  Try https://github.com/capawesome-team/capacitor-firebase/blob/main/packages/authentication/docs/setup-google.md#ios
                    await FirebaseAuthentication.signOut();
                    await FirebaseAuthentication.signInWithGoogle();
                    token = (await FirebaseAuthentication.getIdToken()).token;
                    break;
            }

            if (token) {
                this.handleCredentialResponse(token);
            }
        } catch (error) {
            console.error('Error logging in with Google', error);
        }
    }

    async appleLogin(platform: DevicePlatform) {
        try {
            console.log('appleLogin', platform);
            let token: string;
            switch (platform) {
                case 'web':
                    const appleProvider = new OAuthProvider('apple.com');
                    token = await signInWithPopup(this.auth, appleProvider).then((cred) => cred.user.getIdToken());
                    break;
                case 'android':
                case 'ios':
                    await FirebaseAuthentication.signOut();
                    await FirebaseAuthentication.signInWithApple();
                    token = (await FirebaseAuthentication.getIdToken()).token;
                    break;
            }

            if (token) {
                this.handleCredentialResponse(token);
            }
        } catch (error) {
            console.error('Error logging in with Apple', error);
        }
    }

    async facebookLogin(platform: DevicePlatform) {
        try {
            console.log('facebookLogin', platform);
            let token: string;
            switch (platform) {
                case 'web':
                    const facebookProvider = new FacebookAuthProvider();
                    token = await signInWithPopup(this.auth, facebookProvider).then((cred) => cred.user.getIdToken());
                    break;
                case 'android':
                case 'ios':
                    await FirebaseAuthentication.signOut();
                    await FirebaseAuthentication.signInWithFacebook();
                    token = (await FirebaseAuthentication.getIdToken()).token;
                    break;
            }

            if (token) {
                this.handleCredentialResponse(token);
            }
        } catch (error) {
            console.error('Error logging in with Facebook', error);
        }
    }

    async registerWithEmail(email: string, password: string): Promise<UserCredential> {
        return createUserWithEmailAndPassword(this.auth, email, password);
    }

    async signInWithEmail(email: string, password: string): Promise<UserCredential> {
        return signInWithEmailAndPassword(this.auth, email, password);
    }

    async sendVerificationEmail(user: FirebaseUser): Promise<void> {
        return sendEmailVerification(user);
    }

    async requestPasswordReset(email: string): Promise<void> {
        return sendPasswordResetEmail(this.auth, email);
    }

    async confirmNewPassword(oobCode: string, newPassword: string): Promise<void> {
        return confirmPasswordReset(this.auth, oobCode, newPassword);
    }

    /**
     * Emits the signed-in user, and again whenever that changes.
     *
     * Firebase restores the persisted user asynchronously, so `auth.currentUser`
     * is still null for a moment after a page load. Anything reading it once
     * during initialisation sees null and never corrects itself; this is the
     * seam to use instead. Exposed here rather than importing authState directly
     * at each call site so consumers depend on an injectable they can replace in
     * tests, in keeping with the rest of this service.
     */
    get authState$(): Observable<FirebaseUser | null> {
        return authState(this.auth);
    }

    async linkCredential(user: FirebaseUser, credential: AuthCredential): Promise<UserCredential> {
        return linkWithCredential(user, credential);
    }

    async unlinkProvider(user: FirebaseUser, providerId: string): Promise<FirebaseUser> {
        return unlink(user, providerId);
    }
}
